import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Award,
  AlertTriangle,
  Users,
} from "lucide-react";
import Nav from "../Components/Nav/Nav";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import Login from "./Login";
import ImageProfil from "../Components/Profil/ImageProfil";
import { getFormationResults } from "../actions/formations.actions";

const statusLabel = (s) =>
  s === "completed" ? "Terminé" : s === "suspended" ? "À repasser" : "En cours";

const AdminFormationResults = () => {
  const { id } = useParams();
  const userData = useSelector((s) => s.userReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [openUser, setOpenUser] = useState(null);

  const isAllowed = userData.isAdmin || userData.isReferent;

  useEffect(() => {
    dispatch(getFormationResults(id))
      .then(setData)
      .catch(() => setError(true));
  }, [dispatch, id]);

  if (!userData || !userData.id || !isAllowed) return <Login />;

  const results = data && Array.isArray(data.results) ? data.results : [];
  const completedCount = results.filter((r) => r.status === "completed").length;

  return (
    <>
      <Nav />
      <div className="mainContainer">
        <AdminMenu />
        <div className="centerBlock_admin">
          <AdminFormHeader
            title={`Résultats${data && data.formation ? ` — ${data.formation.title}` : ""}`}
            subtitle="Suivi de la progression des apprenants sur cette formation."
            back={
              <button className="mini-btn" onClick={() => navigate("/admin/formations")}>
                <ArrowLeft size={14} /> Retour
              </button>
            }
          />

          {error ? (
            <p className="builder-empty">Impossible de charger les résultats.</p>
          ) : !data ? (
            <p className="builder-empty">Chargement…</p>
          ) : results.length === 0 ? (
            <div className="results-empty">
              <Users size={40} />
              <p>Personne n'a encore commencé cette formation.</p>
            </div>
          ) : (
            <>
              <div className="results-stats">
                <span>
                  <Users size={15} /> {results.length} apprenant{results.length > 1 ? "s" : ""}
                </span>
                <span>
                  <Award size={15} /> {completedCount} badge{completedCount > 1 ? "s" : ""} obtenu
                  {completedCount > 1 ? "s" : ""}
                </span>
              </div>

              <div className="results-list">
                {results.map((r) => {
                  const open = openUser === r.user.id;
                  return (
                    <div className={`result-card ${open ? "open" : ""}`} key={r.user.id}>
                      <div
                        className="result-row"
                        onClick={() => setOpenUser(open ? null : r.user.id)}
                      >
                        <div className="result-user">
                          <ImageProfil id={r.user.id} />
                          <div>
                            <p className="result-name">
                              {r.user.firstname} {r.user.name}
                            </p>
                            <span className="result-email">{r.user.email}</span>
                          </div>
                        </div>

                        <div className="result-progress">
                          <div className="bar">
                            <span style={{ width: `${r.percent}%` }} />
                          </div>
                          <span className="result-pct">{r.percent}%</span>
                        </div>

                        <span className={`status-pill ${r.status}`}>{statusLabel(r.status)}</span>

                        {r.certificate === "valid" ? (
                          <span className="result-badge valid">
                            <Award size={14} /> Badge
                          </span>
                        ) : r.certificate === "suspended" ? (
                          <span className="result-badge suspended">
                            <AlertTriangle size={14} /> Suspendu
                          </span>
                        ) : (
                          <span className="result-badge none">—</span>
                        )}

                        <span className="result-att-count">
                          {r.attempts.length} quizz tenté{r.attempts.length > 1 ? "s" : ""}
                        </span>

                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>

                      {open ? (
                        <div className="result-detail">
                          {r.attempts.length === 0 ? (
                            <p className="builder-empty">Aucune tentative de quizz pour le moment.</p>
                          ) : (
                            r.attempts.map((a, ai) => (
                              <div className="attempt" key={ai}>
                                <div className="attempt-head">
                                  <strong>{a.quizTitle}</strong>
                                  <span className={`attempt-score ${a.passed ? "pass" : "fail"}`}>
                                    {a.score}% — {a.passed ? "Réussi" : "Échoué"}
                                  </span>
                                </div>
                                {a.questions.map((q, qi) => (
                                  <div className="attempt-q" key={qi}>
                                    <p className="attempt-q-text">
                                      {q.isCorrect ? (
                                        <CheckCircle2 size={15} className="ok" />
                                      ) : (
                                        <XCircle size={15} className="ko" />
                                      )}
                                      {q.question}
                                    </p>
                                    <div className="attempt-options">
                                      {q.options.map((o, oi) => (
                                        <span
                                          key={oi}
                                          className={`opt ${o.correct ? "correct" : ""} ${
                                            o.selected ? "selected" : ""
                                          }`}
                                        >
                                          {o.text}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminFormationResults;
